import React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import { PriceAsCentsInput } from '../stateful/PriceAsCentsInput';
import { IHeadlineLineItemBreakdownLineItem } from 'services/BookingManagerApi';
import { ClosingProductsRow } from './ClosingProductsRow';
import { RemoveHeadlineButton } from './HeadlineActionButtons';
import { isNil } from 'lodash-es';
import { theme } from '../../../tailwind.config';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import classNames from 'classnames';
import { ExistingDirectoryEntriesFullSearch } from 'containers/CRM/components/ExistingDirectoryEntriesSearch/ExistingDirectoryEntriesFullSearch';

const BREAKDOWN_COMPANY_TYPES = [
  'ACT', // Activities
  'BANK', // Banking Suppliers
  'CAR', // Car Hire
  'FINANCE', // Financial Services Supplier
  'GS', // Ground Services
  'GUIDE', // Guides
  'OTHER', // Other
  'PRO', // Professional Services
  'PROP', // Property Supplier
  'REST', // Restaurants
  'TRANSFER', // Transfers
  'YACHT', // Yachts
];

interface IAdditionalChargesBlock {
  pathPrefix?: string;
  className?: string;
  productType: string;
  lineItems: IHeadlineLineItemBreakdownLineItem[];
  bookingCurrencySymbol?: string | null;
  subTotalCents: number;
  includeTableHeaders?: boolean;
  allowNegative?: boolean;
  setHeadlineLineItemFieldAction: (setPath: string, value: string) => void;
  removeHeadlineLineItemAction: (removePath: string, index: number) => void;
  isPurchaseCostEditable: boolean;
  titleJSX: React.ReactElement;
  isEditable: boolean;
  cta?: React.ReactElement;
  isTa: boolean;
  isSR: boolean;
  isAdmin: boolean;
  isFinanceUser: boolean;
  isFinanceWithCompletedStatus: boolean;
  isLiveRate?: boolean;
  titlePlaceholder?: string;
  tertiaryTextPlaceholder?: string;
  isAccommodationSubBlock?: boolean;
  isSalesCostEditable?: boolean;
}

// necessary because we dont have placeholder utility classes
const LineItemInput = styled.input`
  &::placeholder {
    font-style: italic;
  }
`;

export const AdditionalChargesBlock = (props: IAdditionalChargesBlock) => {
  const {
    titleJSX,
    pathPrefix = '',
    lineItems,
    includeTableHeaders = false,
    productType,
    subTotalCents,
    allowNegative,
  } = props;

  const isEditable = props.isEditable && !props.isLiveRate;
  const isInternalUser = props.isAdmin || props.isSR || props.isFinanceUser;
  const isPurchaseCostVisible = props.isSR || props.isFinanceUser;

  const isPurchaseCostDisabled = !props.isPurchaseCostEditable;

  const isSalesCostEditable = isNil(props.isSalesCostEditable) ? props.isEditable : props.isSalesCostEditable;

  const dispatch = useDispatch();

  return (
    <>
      <span className="block mb-10px">{titleJSX}</span>

      {/* the actual line items */}
      <div className="flex flex-col gap-[20px]">
        {lineItems.length >= 1 &&
          lineItems.map((lineItem, index) => {
            // OWA-8928. isEditable inside item forces edition for this item in product blocks not editable
            const isThisItemEditable = isEditable === true || (lineItem.isEditable === true && isInternalUser);
            const keyPrefix = Math.random();
            return (
              <React.Fragment key={index}>
                <div
                  data-index={index}
                  className={classNames('relative grid gap-20px items-center group', {
                    'grid-cols-[1fr_1fr_110px_24px]': !props.isTa,
                    'grid-cols-[1fr_1fr_110px]': props.isTa,
                  })}
                >
                  {/* Additional Charge label */}
                  <h2 className="text-17px text-left m-0">Additional Charge {index + 1}</h2>

                  {/* for spacing and layout */}
                  {!isPurchaseCostVisible && <div className="justify-self-end w-[400px] relative"></div>}

                  {/* supplier search for Additional Charges */}
                  {isPurchaseCostVisible && (
                    <div className="justify-self-end w-[400px] relative">
                      <span className="hidden group-data-[index='0']:block text-gray-darker text-xs uppercase font-pt-sans text-left absolute left-0 -top-5">
                        SUPPLIER
                      </span>
                      <ExistingDirectoryEntriesFullSearch
                        value={lineItem.crmSupplierInformation?.id || ''}
                        onChange={(supplierId, supplier) => {
                          props.setHeadlineLineItemFieldAction(
                            `${pathPrefix}${productType}.items[${index}].crmSupplierInformation`,
                            supplier as any
                          );
                        }}
                        companyTypeCodes={BREAKDOWN_COMPANY_TYPES}
                        disabled={!isEditable}
                      />
                    </div>
                  )}

                  <span className="relative">
                    <span className="hidden group-data-[index='0']:block text-gray-darker text-xs uppercase font-pt-sans text-right absolute right-0 -top-5">
                      CONFIRMATION N.
                    </span>
                    <input
                      placeholder={'Confirmation N.'}
                      value={lineItem.itemConfirmationNumber || ''}
                      type="text"
                      readOnly={!isThisItemEditable}
                      className={classnames(
                        'w-full conf-number focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 font-pt-sans max-h-[35px]',
                        {
                          'bg-gray-10': !isThisItemEditable,
                        }
                      )}
                      onChange={event => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].itemConfirmationNumber`,
                          event.target.value
                        );
                      }}
                    />
                  </span>

                  {/* remove cta */}
                  {isThisItemEditable && (
                    <span className="block">
                      <RemoveHeadlineButton
                        action={props.removeHeadlineLineItemAction}
                        pathPrefix={pathPrefix}
                        productType={productType}
                        index={index}
                      />
                    </span>
                  )}
                </div>

                <div
                  className={classNames('relative group grid gap-20px items-center', {
                    'grid-cols-[1fr_1fr_110px_110px_24px]': !props.isTa,
                    'grid-cols-[1fr_1fr_110px_110px]': props.isTa,
                  })}
                >
                  {/* title */}
                  <HidingTextTooltip tooltipContent={lineItem.title} position="bottom">
                    <LineItemInput
                      disabled={!isThisItemEditable}
                      className={classnames(
                        'focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': !isThisItemEditable,
                        }
                      )}
                      value={lineItem.title}
                      placeholder={props.titlePlaceholder || 'Line Item Title'}
                      onChange={event => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].title`,
                          event.target.value
                        );
                      }}
                    />
                  </HidingTextTooltip>

                  {/* tertiary text */}
                  <HidingTextTooltip tooltipContent={lineItem.tertiaryText} position="bottom">
                    <LineItemInput
                      disabled={!isThisItemEditable}
                      className={classnames(
                        'focus:outline-gray-80 text-black details border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': !isThisItemEditable,
                        }
                      )}
                      value={lineItem.tertiaryText}
                      placeholder={props.tertiaryTextPlaceholder || 'Line Item Description'}
                      onChange={event => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].tertiaryText`,
                          event.target.value
                        );
                      }}
                    />
                  </HidingTextTooltip>

                  {/* purchase cost */}
                  <div className="relative">
                    {isPurchaseCostVisible && includeTableHeaders && (
                      <label className="absolute -top-[36px] right-0 uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">
                        Purchase Cost
                      </label>
                    )}
                    {isPurchaseCostVisible && (
                      <PriceAsCentsInput
                        disabled={isPurchaseCostDisabled}
                        allowNegative={allowNegative}
                        key={`${keyPrefix}-${index}-purchaseCostCents`}
                        className={classnames(
                          'focus:outline-gray-80 bg-blue-10  text-black cost-before-purchase-cost border text-base text-right border-solid border-gray-40 p-2 w-full font-pt-sans',
                          {
                            'bg-gray-10': props.isPurchaseCostEditable === false,
                          }
                        )}
                        cents={lineItem.purchaseCostCents || 0}
                        onBlurUpdate={val => {
                          props.setHeadlineLineItemFieldAction(
                            `${pathPrefix}${productType}.items[${index}].purchaseCostCents`,
                            val
                          );
                        }}
                        onChange={e => {
                          if (props.isAccommodationSubBlock) {
                            dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* cost to tp */}
                  <div className="relative">
                    {includeTableHeaders && (
                      <label className="absolute -top-[36px] right-0 uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">
                        Cost to TP
                      </label>
                    )}
                    <PriceAsCentsInput
                      disabled={!isSalesCostEditable}
                      allowNegative={allowNegative}
                      key={`${keyPrefix}-${index}-saleCostCents`}
                      className={classnames(
                        'focus:outline-gray-80 text-black cost-to-ta border text-base text-right border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': !isSalesCostEditable,
                        }
                      )}
                      cents={lineItem.saleCostCents || 0}
                      onBlurUpdate={val => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].saleCostCents`,
                          val
                        );
                      }}
                      onChange={e => {
                        if (props.isAccommodationSubBlock) {
                          dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                        }
                      }}
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
      </div>
      <ClosingProductsRow
        productDisplayType={productType}
        subTotalCents={subTotalCents}
        bookingCurrencySymbol={props.bookingCurrencySymbol}
        cta={props.cta}
        hasItems={false}
        isEditable={props.isEditable}
      />
    </>
  );
};
